import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertDescription, AlertTitle } from '../../components/ui/alert';
import { 
  AlertTriangle, 
  Info, 
  DollarSign, 
  CheckCircle, 
  AlertCircle, 
  Shield, 
  ShieldCheck, 
  XCircle, 
  Trash2, 
  Search, 
  Smile, 
  Gift
} from 'lucide-react';
import useNews from '../../hooks/useNews';
import { formatDistanceToNow } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { es, enUS, fr } from 'date-fns/locale';
import type { Locale } from 'date-fns';

const localeMap: Record<string, Locale> = {
  es: es,
  en: enUS,
  fr: fr,
};

const NewsSection: React.FC = () => {
  const { news, error } = useNews();
  const { t, i18n } = useTranslation();
  const currentLocale = localeMap[i18n.language] || enUS;

  // Mapas de estilos e íconos...
  const styleMap: Record<string, string> = {
    WELCOME: 'border-l-green-500 text-green-500',
    PAYMENT_REPORT_MADE: 'border-l-green-500 text-green-500',
    PAYMENT_TOKENS_MADE: 'border-l-green-500 text-green-500',
    SIENAGUARD_INIT: 'border-l-blue-500 text-blue-500',
    SIENAGUARD_FINISH: 'border-l-blue-500 text-blue-500',
    SIENAGUARD_ALERT: 'border-l-red-500 text-red-500',
    SIENAGUARD_CLOSE_TO_END: 'border-l-yellow-500 text-yellow-500',
    INVESTIGATION_CREATED: 'border-l-blue-500 text-blue-500',
    INVESTIGATION_DELETED: 'border-l-red-500 text-red-500',
    INVESTIGATION_ACCEPTED: 'border-l-green-500 text-green-500',
    INVESTIGATION_REJECTED: 'border-l-red-500 text-red-500',
    BONUS_TOKEN_GIFTED: 'border-l-purple-500 text-purple-500',
    WELCOME_DISCOUNT_APPLIED: 'border-l-purple-500 text-purple-500'
  };
  
  const iconMap: Record<string, React.ElementType> = {
    WELCOME: Smile,
    PAYMENT_REPORT_MADE: DollarSign,
    PAYMENT_TOKENS_MADE: DollarSign,
    SIENAGUARD_INIT: Shield,
    SIENAGUARD_FINISH: ShieldCheck,
    SIENAGUARD_ALERT: AlertCircle,
    SIENAGUARD_CLOSE_TO_END: AlertTriangle,
    INVESTIGATION_CREATED: Search,
    INVESTIGATION_DELETED: Trash2,
    INVESTIGATION_ACCEPTED: CheckCircle,
    INVESTIGATION_REJECTED: XCircle,
    BONUS_TOKEN_GIFTED: Gift,
    WELCOME_DISCOUNT_APPLIED: Gift
  };
  

  if (error) {
    return <p>{t('news.error')}</p>;
  }

  // Obtén la zona horaria local:
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="news-section">
      <div className="flex justify-center items-center mb-4">
        <Info className="h-6 w-6 text-blue-500 mr-2" />
        <h2 className="text-2xl font-bold">{t('news.title')}</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4 text-center">
        {t('news.subtitle')}
      </p>

      {news.map((item) => {
        const severityClass = styleMap[item.type] || 'border-l-gray-500 text-gray-500';
        const IconComponent = iconMap[item.type] || Info;
        
        // Convertir la fecha UTC a la hora local:
        const localDate = toZonedTime(new Date(item.created_at), timeZone);

        return (
          <div
            key={item.id}
            className={`p-4 rounded-lg shadow-sm border-l-4 ${severityClass} mb-4 bg-gray-50 flex items-center hover:bg-gray-100 transition-colors duration-200`}
          >
            <div className="mr-4 flex-shrink-0">
              <IconComponent className={`h-8 w-8 ${severityClass}`} />
            </div>
            <div>
              <AlertTitle className="text-base font-semibold mb-2">
                {item.title}
              </AlertTitle>
              <AlertDescription>
                <p className="text-sm text-gray-600 mb-1">
                  {item.message}
                </p>
                <p className="text-xs text-gray-400">
                  {formatDistanceToNow(localDate, { addSuffix: true, locale: currentLocale })}
                </p>
              </AlertDescription>
            </div>
          </div>
        );
      })}

      <style>{`
        .news-section {
          max-height: 750px;
          overflow-y: auto;
          background-color: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        .news-section::-webkit-scrollbar {
          width: 10px;
        }
        .news-section::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .news-section::-webkit-scrollbar-thumb {
          background-color: #cbd5e0;
          border-radius: 10px;
          border: 2px solid #f1f1f1;
        }
        .news-section::-webkit-scrollbar-thumb:hover {
          background-color: #a0aec0;
        }
      `}</style>
    </div>
  );
};

export default NewsSection;
